import React, { useMemo, useState } from 'react';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import LightBoxImage from '~/app/common/LightBoxImage';
import { Attachment as WorkOrderAttachment } from '~/workorder/types';
import { isWorkOrderAttachmentImage } from '~/workorder/helper';
import classes from './style.module.scss';

export interface AttachmentProps {
    attachments: WorkOrderAttachment[];
}

export type AttachmentComponent = React.FC<AttachmentProps>;

const Attachment: AttachmentComponent = ({ attachments }) => {
    const { t } = useTranslation('workorder');
    const [expanded, setExpanded] = useState(false);

    const { images, others, count } = useMemo(() => {
        const images: WorkOrderAttachment[] = [];
        const others: WorkOrderAttachment[] = [];
        attachments.forEach(item => {
            if (isWorkOrderAttachmentImage(item.mimeType)) {
                images.push(item);
            } else {
                others.push(item);
            }
        });
        return { images, others, count: attachments.length };
    }, [attachments]);

    return (
        <div className={classes['card']}>
            <div className={classes['header']} onClick={() => setExpanded(prev => !prev)}>
                <h2 className={classes['header-title']}>
                    <Icon name="description" size={20} className={classes['header-icon']} />
                    {t('detail.attachments')} ({count})
                </h2>
                <Icon name={expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} className={classes['chevron']}/>
            </div>

            {expanded && (
                count === 0 ? (
                    <p className={classes['empty']}>{t('detail.noAttachmentAdded')}</p>
                ) : (
                    <>
                        {images.length > 0 && (
                            <div className={classes['section']}>
                                <p className={classes['section-label']}>{t('detail.attachmentImages')}</p>
                                <div className={classes['attachment-images']}>
                                    {images.map(image => (
                                        <div key={image.id} className={classes["workorder-image"]}>
                                            <LightBoxImage image={image.url} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {others.length > 0 && (
                            <div className={classes['section']}>
                                <p className={classes['section-label']}>{t('detail.attachmentOthers')}</p>
                                <div className={classes['pdf-preview-list']}>
                                    {others.map(pdf => (
                                        <a
                                            key={pdf.id}
                                            href={pdf.url}
                                            className={classes['pdf-preview-item']}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <Icon name="description" className={classes['pdf-icon']} />
                                            <div className={classes['pdf-info']}>
                                                <div className={classes['pdf-name']}>{pdf.name}</div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )
            )}
        </div>
    );
};

export default Attachment;
